import { useEffect, useRef, useState } from "react"

import { UploadOutlined } from "@ant-design/icons"
import { App, Avatar, Col, Divider, Flex, Modal, Row, Space, Spin, Typography, Upload } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Navigate, useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import InputComponent from "../../Components/Input"
import PhoneInput from "../../Components/PhoneInput"
import { INCOGNITO_MASK } from "../../Components/Private"
import TitleSection from "../../Components/TitleSection/TitleSection"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import Icon from "../../Icon"
import { selectIncognito } from "../../Utils/incognito"
import { validatePhone } from "../../Utils/onboarding/validatePhone"
import { getCachedCountries } from "../../Utils/phoneCountriesCache"
import { getBase64, useQuery } from "../../Utils/utils"
import AuthActions from "../LoginPage/reducer"
import UserCreators from "../User/reducer"
import ChangeEmailForm from "./MyProfile/ChangeEmailForm"
import ChangePasswordForm from "./MyProfile/ChangePasswordForm"
import CheckEmail from "./MyProfile/CheckEmail"
//import VerifyEmailForm from './MyProfile/VerifyEmailForm'
import CreateNewPassword from "./MyProfile/CreateNewPassword"

const { Text } = Typography

const usePageQuery = (search) => {
  const query = useQuery(search)
  return {
    action: query.get("action"),
    code: query.get("code")
  }
}

const isPhoneChanged = (prevPhone, nextPhone) =>
  prevPhone?.id !== nextPhone?.id || prevPhone?.number !== nextPhone?.number

const getPhoneUpdateBody = (phone) => ({
  phone: `+${phone.code}${phone.number}`,
  country_name: phone.name,
  phone_code_id: phone.id,
  countryCode: phone?.country_code
})

export const MyProfile = ({
  saveFetching,
  userInfo,
  updateUser,
  updateAvatar,
  saveResult,
  saveAvatarFetching,
  userSaveResultReset,
  isMobile,
  resetError,
  error,
  changePassword,
  userError,
  userErrorsReset,
  sendCodePassword,
  //confirmCodePassword,
  createNewPassword,
  changeEmails,
  setPassword,
  confirmEmail,
  incognito
}) => {
  const {
    email,
    first_name,
    last_name,
    avatar,
    password_created,
    onboarding_finished,
    new_onboarding_finished
  } = userInfo

  const { search } = useLocation()
  const { action, code } = usePageQuery(search)
  const navigate = useNavigate()
  const externalDemoMode = useExternalDemoMode()
  const countries = getCachedCountries() || []
  const isDemoModeBlocked = !onboarding_finished && !new_onboarding_finished

  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState(false)

  const { t } = useTranslation()
  const { notification } = App.useApp()

  const data = useRef({
    first_name,
    last_name
  })
  const [field, setField] = useState("")
  const [modal, setModal] = useState("")
  const [saveConfirm, setSaveConfirm] = useState({})
  const [modalWidth, setModalWidth] = useState(460)

  const [avatarError, setAvatarError] = useState("")

  const [phone, setPhone] = useState(getPhoneData())
  const [phoneError, setPhoneError] = useState(null)

  const changedField = {
    first_name: t("First Name"),
    last_name: t("Last Name"),
    password: t("Password"),
    email: t("Email")
  }

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>
  }

  const notificationSuccessText = (field) => {
    if (field === "email") {
      return localStorage.getItem("dropshipVerifyEmail") === "true" && field === "email"
        ? { message: t("Your Email has been successfully verified") }
        : { message: t("Email changed"), description: t("Your email was changed successfully.") }
    } else if (field === "password") {
      return {
        message: t("Password changed"),
        description: t("Your password was changed successfully.")
      }
    } else if (field === "phone") {
      return { message: t("Your phone number was changed successfully.") }
    } else {
      return {
        message: t("Your _field_ was changed successfully", {
          field: field?.includes("name") ? t("name") : changedField[field]
        })
      }
    }
  }

  function getPhoneData() {
    if (userInfo?.phone) {
      const phone = userInfo?.phone
      const phoneCode = userInfo?.phone_code
      const codeLength = phoneCode?.code.length + 1
      return {
        countryCode: phoneCode?.country_code,
        code: phoneCode.code,
        number: phone.slice(codeLength),
        name: phoneCode.name,
        id: phoneCode.id
      }
    }
    return {
      countryCode: countries[19]?.country_code,
      code: countries[19]?.code,
      number: "",
      name: countries[19]?.name,
      id: countries[19]?.id
    }
  }

  useEffect(() => {
    if (code && action === "forgot_password") {
      handleChangeModal("createNewPassword")
      setVisible(true)
    } else if (code && action === "change_email") {
      confirmEmail({
        hash: code,
        isVerify: false
      })
    }
  }, [action])

  useEffect(() => {
    if (error?.message) {
      notification.error({
        message: error?.message?.detail
      })
    }
    if (error) resetError()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  useEffect(() => {
    if (saveResult && !saveResult?.message) {
      if (field) {
        let message = notificationSuccessText(field)
        notification.success({
          ...message,
          ...notificationConfig,
          style: { minWidth: "420px" }
        })
        setField("")
        userSaveResultReset()
      }
      if (saveResult.avatar) {
        let message =
          saveResult.avatar === "delete"
            ? "Your avatar was removed successfully"
            : "Avatar was replaced successfully"
        notification.success({
          message: t(message),
          ...notificationConfig,
          style: { minWidth: "420px" }
        })
        userSaveResultReset()
      }
    }
    if (userError) {
      if (userError?.confirmError?.detail) {
        notification.error({
          message: userError?.confirmError?.detail,
          ...notificationConfig
        })
        userErrorsReset()
      } else if (userError?.userPersonalError) {
        let message = userError?.userPersonalError?.errors?.detail
        if (typeof message !== "string") message = t("Something went wrong, kindly try again")
        notification.error({
          message,
          ...notificationConfig
        })
        userErrorsReset()
      }
      if (userError?.avatarError) {
        notification.error({
          message: userError?.avatarError?.detail,
          ...notificationConfig
        })
        userErrorsReset()
      }
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult, userError, field])

  useEffect(() => {
    if (saveResult?.confirmEmail) {
      changePersonalData("email")
    }
    if (saveResult?.setPassword) {
      notification.success({
        message: t("A password has been successfully set!"),
        ...notificationConfig,
        style: { minWidth: "420px" }
      })
      setVisible(false)
      userSaveResultReset()
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult])

  function handleSubmit(current, new_data) {
    if (modal === "changePassword") {
      changePassword({ current, password: new_data })
      changePersonalData("password")
    } else if (modal === "changeEmail") {
      changeEmails({ password: current, new_email: new_data })
    }
  }

  function handleCreatePasswordSubmit(password) {
    if (password_created) {
      createNewPassword({ password, confirm_code: code })
      changePersonalData("password")
    } else {
      setPassword({ password })
    }
  }

  function handleClose() {
    setVisible(false)
    if (modal === "createNewPassword" && code) navigate("/settings/my_profile", { replace: true })
  }

  function handleChangeModal(name) {
    userErrorsReset()
    userSaveResultReset()
    setModal(name)

    if (name === "forgotPassword") {
      setModalWidth(442)
    } else if (name === "checkEmail") {
      setModalWidth(442)
    } else {
      setModalWidth(460)
    }
  }

  function handleSaveConfirm(data) {
    setSaveConfirm(data)
  }

  const fakeRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok")
    }, 500)
  }

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png"
    if (!isJpgOrPng) {
      setAvatarError(
        "You can upload file only in JPG/ PNG formats, please select a different image."
      )
      setLoading(false)
      return false
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      setAvatarError(
        "Image upload failed: your image is bigger than 10MB, please select a different image."
      )
      setLoading(false)
      return false
    }
    if (avatarError) setAvatarError("")
    return true
  }

  const handleChangeImage = (info = null) => {
    if (info === null) {
      if (avatarError) setAvatarError("")
      setLoading(false)
      updateAvatar({ avatar: { media: "" } })
      return
    }
    if (info.file.status === "uploading") {
      setLoading(true)
      return
    }
    if (info.file.status === "done") {
      getBase64(info.file.originFileObj, () => {
        setLoading(false)
        updateAvatar({ avatar: { media: info.file.originFileObj } })
      })
    }
  }

  function handleUpdateUserInfo(params) {
    if (
      params === "first_name" &&
      first_name !== data.current.first_name &&
      data.current.first_name
    ) {
      updateUser({ [params]: data.current[params] })
    } else if (
      params === "last_name" &&
      last_name !== data.current.last_name &&
      data.current.last_name
    ) {
      updateUser({ [params]: data.current[params] })
    } else {
      return
    }

    changePersonalData(params)
  }

  const handleSetPhone = (phone) => {
    setPhoneError(null)
    setPhone(phone)
  }

  function handleUpdatePhoneNumber() {
    const error = validatePhone(phone, t)

    if (error) {
      setPhoneError(error)
      return
    }

    setPhoneError(null)

    const oldPhone = getPhoneData()
    if (!isPhoneChanged(oldPhone, phone)) return

    submitPhoneUpdate(phone)
  }

  function submitPhoneUpdate(phone) {
    userSaveResultReset()
    setField("phone")
    updateUser(getPhoneUpdateBody(phone))
  }

  function changePersonalData(params) {
    setField(params)
  }

  function handleSelect(value, fieldName) {
    data.current = { ...data.current, [fieldName]: value.trim() }
  }

  function handleSendCodePassword() {
    sendCodePassword(email)
  }

  function handleChangeEmails() {
    changeEmails({ password: saveConfirm?.current || "", new_email: saveConfirm?.email || email })
  }

  //const defaultAvatar = (<Icon type="user_default" isMobile={isMobile} role="icon" width={64} height={64}/>)

  const changePasswordBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {t("Your new password must be different from previously used passwords.")}
      </Text>

      <ChangePasswordForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        userError={userError}
        userErrorsReset={userErrorsReset}
        saveResult={saveResult}
        userSaveResultReset={userSaveResultReset}
        onChangeModal={handleChangeModal}
        sendCodePassword={handleSendCodePassword}
        setField={setField}
        isMobile={isMobile}
      />
    </div>
  )

  const createNewPasswordBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {password_created
          ? t("Your new password must be different from previously used passwords.")
          : null}
      </Text>
      <CreateNewPassword
        password_created={password_created}
        onSubmit={handleCreatePasswordSubmit}
        onClose={handleClose}
        userError={userError}
        userErrorsReset={userErrorsReset}
        userSaveResultReset={userSaveResultReset}
        saveResult={saveResult}
      />
    </div>
  )

  /*const verifyEmailBlock = (
    <VerifyEmailForm
      onClose={handleClose}
      onChangeModal={handleChangeModal}
      sendCodePassword={handleSendCodePassword}
      confirmCodePassword={confirmCodePassword}
      userErrorsReset={userErrorsReset}
      userFetching={saveFetching}
      userError={userError}
      saveResult={saveResult}
      userSaveResultReset={userSaveResultReset}
      email={email}
    />
  )*/

  const changeEmailBlock = (
    <div>
      <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
        {t(
          "Before proceeding, we kindly ask you to provide your current password and new email address."
        )}
      </Text>
      <ChangeEmailForm
        onSubmit={handleSubmit}
        onClose={handleClose}
        userError={userError}
        userErrorsReset={userErrorsReset}
        saveResult={saveResult}
        userSaveResultReset={userSaveResultReset}
        onChangeModal={handleChangeModal}
        onSaveConfirm={handleSaveConfirm}
        setField={setField}
        email={email}
        isMobile={isMobile}
      />
    </div>
  )

  const checkEmailBlock = (
    <div>
      <CheckEmail
        data={saveConfirm}
        onResend={handleChangeEmails}
        email={email}
        extraText={
          !saveConfirm?.email
            ? null
            : t("The new email address won’t take effect until it is confirmed.")
        }
      />
    </div>
  )

  const forgotPasswordBlock = (
    <div>
      <CheckEmail data={{ email }} onResend={handleSendCodePassword} email={email} />
    </div>
  )

  const modalBlocks = {
    changePassword: {
      component: changePasswordBlock,
      title: t("Change Password")
    },
    forgotPassword: {
      component: forgotPasswordBlock,
      title: null
    },
    createNewPassword: {
      component: createNewPasswordBlock,
      title: password_created ? t("Create new password") : t("Create password")
    },
    changeEmail: {
      component: changeEmailBlock,
      title: t("Change Email")
    },
    checkEmail: {
      component: checkEmailBlock,
      title: null
    }
  }

  if (code && action === "change_email") {
    return <Navigate to="/settings/my_profile" replace />
  }

  return (
    <div>
      <TitleSection
        icon="user"
        title="Profile"
        description="Adjust your Chargeback profile to your own liking."
        style={{ margin: isMobile ? "-16px -16px 16px" : "-32px -32px 24px" }}
      />

      <Row>
        <Col flex={!isMobile && "1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("Profile picture")}</Text>
            <Text type="secondary">
              {t("Personalize your profile to reflect your individual style with ease.")}
            </Text>
          </Space>
        </Col>
        <Col flex={!isMobile && "0 1 420px"} style={{}}>
          <Space
            size={16}
            style={{
              flexDirection: isMobile && "column",
              alignItems: isMobile && "flex-start",
              paddingTop: isMobile && 12
            }}
          >
            <Avatar
              className="user_avatar"
              size={!isMobile ? 64 : 32}
              style={{
                backgroundColor: avatar ? "transparent" : "#225AEA",
                borderRadius: "32px",
                border: "none"
              }}
              src={userInfo?.avatar}
            >
              {userInfo?.avatar
                ? userInfo?.avatar
                : `${data?.current?.first_name?.[0]}${data?.current?.last_name?.[0]}`}
            </Avatar>

            <Space size={12} direction="vertical" style={{ width: isMobile && "100%" }}>
              <Flex gap={12} style={{ width: isMobile && "100%" }}>
                <Upload
                  name="media"
                  accept=".jpeg,.jpg,.png"
                  className="upload-avatar mobile"
                  showUploadList={false}
                  customRequest={fakeRequest}
                  beforeUpload={beforeUpload}
                  onChange={handleChangeImage}
                  style={{ width: isMobile && "100%" }}
                >
                  <ButtonComponent
                    icon={<UploadOutlined />}
                    loading={loading || saveAvatarFetching}
                    disabled={
                      loading || saveAvatarFetching || externalDemoMode || isDemoModeBlocked
                    }
                    className="flex"
                    style={{ width: isMobile && "100%" }}
                  >
                    {t(loading ? "Uploading" : avatar ? "Replace image" : "Click to Upload")}
                  </ButtonComponent>
                </Upload>

                <ButtonComponent
                  disabled={!avatar}
                  onClick={() => handleChangeImage()}
                  style={{ width: isMobile && "100%" }}
                >
                  {t("Remove")}
                </ButtonComponent>
              </Flex>

              <Space size={8} className="error">
                {avatarError && <Icon type={"alert_triangle_error"} style={{ fontSize: 16 }} />}
                <Text
                  type={avatarError ? "danger" : "secondary"}
                  style={{ fontSize: 12, lineHeight: 1.5 }}
                >
                  {avatarError
                    ? t(avatarError)
                    : !isMobile
                      ? t("*.png, *.jpeg files up to 10MB")
                      : t(
                          "Upload *.png or *.jpeg files, up to 10MB, recommended size is 400x400px."
                        )}
                </Text>
              </Space>
            </Space>
          </Space>
        </Col>
      </Row>

      <Divider style={{ visibility: isMobile && "hidden", margin: "12px 0" }} />

      <Row>
        <Col flex={!isMobile && "1 1 300px"} style={{ paddingBottom: isMobile && 12 }}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("First and last name")}</Text>
            <Text type="secondary">{t("Changing your name will be auto-saved.")}</Text>
          </Space>
        </Col>
        <Col flex={"0 1 420px"}>
          <Flex gap={12} align={"center"} style={{ height: "100%" }}>
            <InputComponent
              placeholder={t("First Name")}
              defaultValue={data.current.first_name || ""}
              onBlur={() => handleUpdateUserInfo("first_name")}
              onChange={(e) => handleSelect(e.target.value, "first_name")}
              disabled={externalDemoMode || isDemoModeBlocked}
              // Incognito masks the value AND whatever is being typed, not just the display.
              type={incognito ? "password" : "text"}
            />

            <InputComponent
              placeholder={t("Last Name")}
              defaultValue={data.current.last_name || ""}
              onBlur={() => {
                handleUpdateUserInfo("last_name")
              }}
              onChange={(e) => handleSelect(e.target.value, "last_name")}
              disabled={externalDemoMode || isDemoModeBlocked}
              type={incognito ? "password" : "text"}
            />
          </Flex>
        </Col>
      </Row>

      <Divider style={{ visibility: isMobile && "hidden", margin: "12px 0" }} />

      <Row>
        <Col flex={!isMobile && "1 1 300px"}>
          <Space size={8} direction={"vertical"} style={{ paddingBottom: isMobile && 12 }}>
            <Text strong>{t("Email address")}</Text>
            <Text type="secondary">{t("You would need to press Edit to change your email.")}</Text>
          </Space>
        </Col>
        <Col flex={"0 1 420px"}>
          <Flex gap={12} align={"center"} style={{ height: "100%" }}>
            <InputComponent placeholder={incognito ? INCOGNITO_MASK : email} disabled />
            <ButtonComponent
              size="large"
              onClick={() => {
                setVisible(true)
                localStorage.setItem("dropshipVerifyEmail", "false")
                handleChangeModal("changeEmail")
              }}
              disabled={externalDemoMode || isDemoModeBlocked}
            >
              {t("Edit")}
            </ButtonComponent>
          </Flex>
        </Col>
      </Row>

      <Divider style={{ visibility: isMobile && "hidden", margin: "12px 0" }} />

      <Row>
        <Col flex={"1 1 300px"} style={{ paddingBottom: isMobile && 12 }}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("Password")}</Text>
            <Text type="secondary">
              {t(
                "We strongly advise you to use a strong password, containing at least 8 characters, with a mix of numbers and letters."
              )}
            </Text>
          </Space>
        </Col>
        <Col flex={"0 1 420px"}>
          <Flex gap={12} align={"center"} style={{ height: "100%" }}>
            <InputComponent
              placeholder={password_created ? "******************" : t("Not Set")}
              disabled
            />
            <ButtonComponent
              size="large"
              onClick={() => {
                setVisible(true)
                handleChangeModal(password_created ? "changePassword" : "createNewPassword")
              }}
              disabled={externalDemoMode || isDemoModeBlocked}
            >
              {password_created ? t("Edit") : t("Add")}
            </ButtonComponent>
          </Flex>
        </Col>
      </Row>

      <Divider style={{ visibility: isMobile && "hidden", margin: "12px 0" }} />

      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"}>
            <Text strong>{t("Phone number")}</Text>
            <Text type="secondary">
              {t("You would need to press Edit to change your phone number")}
            </Text>
          </Space>
        </Col>
        <Col flex={"0 1 420px"}>
          <Flex gap={12} align={"center"} style={{ height: "100%" }}>
            {externalDemoMode ? (
              <InputComponent
                className="phoneInput"
                value="555 123 4567"
                disabled
                addonBefore={
                  <Flex align="center" gap={8}>
                    <span style={{ fontSize: 24, lineHeight: 1 }}>🇺🇸</span>
                    <Text>+1</Text>
                  </Flex>
                }
              />
            ) : (
              <PhoneInput
                value={phone}
                onChange={handleSetPhone}
                onBlur={handleUpdatePhoneNumber}
                status={phoneError ? "error" : ""}
                disabled={isDemoModeBlocked}
                type={incognito ? "password" : "text"}
              />
            )}
          </Flex>
          {phoneError && <Text type="danger">{phoneError}</Text>}
        </Col>
      </Row>

      <Divider />

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={visible}
        centered={!isMobile}
        width={modalWidth}
        title={modalBlocks[modal]?.title}
        footer={null}
        closable="true"
        onCancel={handleClose}
        destroyOnClose
        classNames={{
          content: cls("clear-bg", {
            "bg-center": modal === "checkEmail"
          })
        }}
        getContainer={() => document.getElementById("main-layout")}
      >
        <Spin size="large" spinning={saveFetching}>
          {modalBlocks[modal]?.component}
        </Spin>
      </Modal>
    </div>
  )
}

MyProfile.defaultProps = {}

MyProfile.propTypes = {
  fetching: PropTypes.bool,
  isMobile: PropTypes.bool
}

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
  isMobile: state.nav.isMobile,

  saveFetching: state.user.saveFetching,
  saveAvatarFetching: state.user.saveAvatarFetching,
  saveResult: state.user.saveResult,
  error: state.auth.error,
  userError: state.user.errors,
  incognito: selectIncognito(state)
})

const mapDispatchToProps = (dispatch) => ({
  updateUser: (data) => dispatch(UserCreators.userSaveRequest(data)),
  updateAvatar: (data) => dispatch(UserCreators.userSaveAvatarRequest(data)),

  userSaveResultReset: () => dispatch(UserCreators.userSaveResultReset()),
  resetError: () => dispatch(AuthActions.authErrorReset()),
  changePassword: (password) => dispatch(UserCreators.changePasswordRequest(password)),
  setPassword: (password) => dispatch(UserCreators.setPasswordRequest(password)),
  userErrorsReset: () => dispatch(UserCreators.userErrorsReset()),

  sendCodePassword: (email) => dispatch(UserCreators.sendCodePasswordRequest(email)),
  //confirmCodePassword: (code) => dispatch(UserCreators.confirmCodePasswordRequest(code)),

  createNewPassword: (data) => dispatch(UserCreators.createNewPasswordRequest(data)),

  changeEmails: (email) => dispatch(UserCreators.changeEmailsRequest(email)),
  confirmEmail: (hash, isVerify) => dispatch(UserCreators.confirmEmailsRequest(hash, isVerify))
})

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile)
